import { createSelector } from "reselect";

import { scale } from "app/styles";
import {
  DEFAULT_COLORS,
  DEFAULT_FONT_SIZES,
  DEFAULT_FONT_STYLES,
} from "consts";
import { DEFAULT_LOGO_CONFIG } from "consts/configs";
import { humanReadableList } from "utils";

import {
  getLanguageKey,
  getSelectedQuestionOptions,
} from "screens/question/selectors";

export const getQuestionnaires = state => state.questionnaires;

export const getSurveyQuestionnaire = createSelector(
  [state => state.survey.questionnaireId, getQuestionnaires],
  (qId, questionnaires) => questionnaires.find(q => q.id === qId),
);

export const getQuestionsFromQuestionnaire = createSelector(
  getSurveyQuestionnaire,
  questionnaire =>
    questionnaire.questionnaire_sections
      .map(section => section.questions)
      .flat(),
);

export const getSurveyDescription = createSelector(
  state => state,
  (_state, questionnaire) => questionnaire,
  (state, questionnaire) => {
    if (!questionnaire?.description) {
      return "";
    }
    return fillPlaceholders(questionnaire.description, state);
  },
);

export const getSurveyBackground = (questionnaire, state) => {
  const _questionnaire = questionnaire || getSurveyQuestionnaire(state);
  const background =
    _questionnaire?.background_image || _questionnaire?.colors?.background;
  return background || state.organization.background;
};

export const fillPlaceholders = (text, state) =>
  placeholders.reduce((txt, placeholder) => {
    const placeholderInstances = txt.match(placeholder.regex);
    if (!placeholderInstances) {
      return txt;
    }
    return placeholderInstances.reduce((tx, match) => {
      const value = placeholder.fill(state, match);
      const regex = placeholder.replace_regex || placeholder.regex;
      return tx.replace(regex, value ? ` ${value}` : "");
    }, txt);
  }, text);

const placeholders = [
  {
    regex: / ?{name}/gi,
    fill: state => (state.customer.title || "") + (state.customer.name || ""),
  },
  {
    regex: / ?{division}/gi,
    fill: state => state.division.title,
  },
  {
    regex: / ?{organization}/gi,
    fill: state => state.organization.name,
  },
  {
    regex: / ?{product}/gi,
    fill: state => state.survey.items[0]?.name,
  },
  {
    regex: / ?{product_category}/gi,
    fill: state => state.survey.items[0]?.category,
  },
  {
    regex: / ?{products}/gi,
    fill: state => humanReadableList(state.survey.items.map(i => i.name)),
  },
  {
    regex: / ?{product_categories}/gi,
    fill: state => humanReadableList(state.survey.items.map(i => i.category)),
  },
  {
    regex: / ?{question_\d*}/gi,
    fill: (state, match) => {
      let [questionId] = match.match(/\d+/) || [];
      if (!questionId) {
        return;
      }
      questionId = parseInt(questionId, 10);
      const lang = getLanguageKey(state);
      const questions = getQuestionsFromQuestionnaire(state);
      const question = questions.find(q => q.virtual_id === questionId);
      const selectedOptions = getSelectedQuestionOptions(state, question);
      return humanReadableList(selectedOptions.map(o => o[lang]));
    },
  },
  {
    regex: / ?{placeholders\.\w+}/gi,
    replace_regex: / ?{placeholders\.\w+}/i,
    fill: (state, match) => {
      const key = match.split(".")[1]?.slice(0, -1);
      const placeholder = state.survey.placeholders[key];
      if (placeholder) {
        return placeholder;
      }
    },
  },
];

export const getSurveyLogo = state => {
  const questionnaire = getSurveyQuestionnaire(state);
  return questionnaire?.logo || state.organization.logo;
};

export const pickTheme = (questionnaire, organization) => {
  const colors = pickColors(questionnaire, organization);
  const fontSizes = pickFontSizes(questionnaire);
  const fontStyles = pickFontStyles(questionnaire);
  const logoConfigs = pickLogoConfigs(questionnaire);

  return { ...colors, ...fontSizes, ...fontStyles, ...logoConfigs };
};

const pickLogoConfigs = questionnaire => {
  const configs = {};

  for (const [key, default_] of Object.entries(DEFAULT_LOGO_CONFIG)) {
    const qSize = questionnaire?.config?.logo_configs?.[key];

    // strings are used as it is, number is scaled
    let size = qSize ? qSize : default_;
    configs["$" + key + "_logo"] = size;
  }

  return configs;
};

const pickFontSizes = questionnaire => {
  const sizes = {};
  const sizeMultiplier = questionnaire?.font_sizes?.default_multiplier || 1;
  for (const [key, default_] of Object.entries(DEFAULT_FONT_SIZES)) {
    const qSize = questionnaire?.font_sizes?.[key];

    // strings are used as it is, number is scaled
    let size = qSize;
    if (typeof qSize === "number") {
      size = scale(qSize);
    } else if (!qSize) {
      size = sizeMultiplier * scale(default_);
    }

    sizes["$" + key + "_size"] = size;
  }
  return sizes;
};

const pickFontStyles = questionnaire => {
  const styles = {};
  const global_font = questionnaire?.font_styles?.default_style;
  for (const [key, default_] of Object.entries(DEFAULT_FONT_STYLES)) {
    const qStyle = questionnaire?.font_styles?.[key];
    const style = [qStyle, global_font, default_].filter(f => f)[0];
    styles["$" + key + "_style"] = style;
  }
  return styles;
};

const pickColors = (questionnaire, organization) => {
  const colors = {};
  for (const [key, value] of Object.entries(DEFAULT_COLORS)) {
    colors["$" + key] =
      questionnaire?.colors?.[key] || organization?.colors?.[key] || value;
  }
  return colors;
};

export const markSelectedItems = (items, linkedOptionForItem, response) => {
  if (!linkedOptionForItem) {
    return items;
  }

  const { questionId, optionId, subOptionId } = linkedOptionForItem;

  const question = response.questions[questionId];
  const option = question?.options[optionId];
  const subOption = option?.subOptions?.[subOptionId];

  if (!option) {
    return items;
  }

  return items.map(item => {
    const o = subOption || option;
    const isSelected = o.products?.includes(item.id);
    return { ...item, isSelected };
  });
};

export const isStandardApp = state =>
  !state.survey.inviteId && !state.survey.isDemo;
